package com.exura.components.layouts.plan

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.plandefinition.domain.builder.plan.BasePlanBuilderNew
import com.exura.features.plandefinition.domain.builder.plan.PlanBuildersNew
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.plan.PreparationPlan
import com.exura.features.plandefinition.domain.model.plan.TreatmentPlan
import com.exura.features.plandefinition.domain.model.series.BaseSeries
import com.exura.features.plandefinition.usecase.CreateTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.DeleteTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.GetTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.GetVersionContentUseCase
import com.exura.features.plandefinition.usecase.UpdateTreatmentPlanUseCase
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.navigation.parameters.currentPageParams
import com.exura.navigation.parameters.currentPathWithParams
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext

data class SideBarElementRenderingData(
    val lowDay: Int?,
    val duration: Int?,
    val offset: Int,

    val spawnedSeriesElements: List<BasePlanElement>,
) {
    val highDay: Int? = lowDay?.let { start -> duration?.let { start + it - 1 } }
}

sealed interface SideBarPlanElement {
    val title: String
    val collectionPos: Int?
    val seriesPos: Int?

    val renderingData: SideBarElementRenderingData

    data class CollectionItem(override val collectionPos: Int, val collectionItem: Collection) : SideBarPlanElement {
        override val seriesPos = null
        override val title: String = collectionItem.name

        override val renderingData: SideBarElementRenderingData = SideBarElementRenderingData(
            lowDay = collectionItem.absoluteStartDay(),
            duration = collectionItem.durationInDays(),
            offset = 0,
            spawnedSeriesElements = emptyList(),
        )
    }

    data class RootSeriesItem(override val seriesPos: Int, val seriesItem: BaseSeries<*>) : SideBarPlanElement {
        override val collectionPos = null
        override val title: String = seriesItem.title

        override val renderingData: SideBarElementRenderingData = SideBarElementRenderingData(
            lowDay = seriesItem.relativeDay,
            duration = seriesItem.durationInDays(),
            offset = 0,
            spawnedSeriesElements = seriesItem.spawnElements(),
        )
    }

    data class NestedSeriesItem(
        override val collectionPos: Int,
        override val seriesPos: Int,
        val collectionItem: Collection,
        val seriesItem: BaseSeries<*>,
    ) : SideBarPlanElement {
        override val title: String = seriesItem.title

        override val renderingData: SideBarElementRenderingData = SideBarElementRenderingData(
            lowDay = seriesItem.relativeDay?.plus(collectionItem.relativeDay),
            duration = seriesItem.durationInDays(),
            offset = collectionItem.relativeDay,
            spawnedSeriesElements = seriesItem.spawnElements(),
        )
    }
}

sealed interface PlanConstructorLayoutEvent {
    data class ChangeLayoutMode(val mode: LayoutMode) : PlanConstructorLayoutEvent
    data class ChangeViewMode(val mode: ViewMode) : PlanConstructorLayoutEvent
    data class ChangeDisplayRange(val range: DisplayRange) : PlanConstructorLayoutEvent
    data object NavigateDisplayForward : PlanConstructorLayoutEvent
    data object NavigateDisplayBackward : PlanConstructorLayoutEvent
    data class UpdatePlanTitle(val newTitle: String) : PlanConstructorLayoutEvent
    data class UpdatePlanDescription(val newDescription: String) : PlanConstructorLayoutEvent
    data class UpdateImageSource(val newSource: String?) : PlanConstructorLayoutEvent
    data object Save : PlanConstructorLayoutEvent
    data object Send : PlanConstructorLayoutEvent

    data class SelectSideBarElement(val element: SideBarPlanElement) : PlanConstructorLayoutEvent
}

data class PlanConstructorLayoutState(
    val isLoadingPlan: Boolean = true,
    val isLoadingPlanContent: Boolean = true,

    val isPlanChanged: Boolean = false,
    val isPlanValid: Boolean = true,
    val title: String = "",
    val description: String = "",
    val imageSrc: String? = null,
    val sideBarItems: List<SideBarPlanElement> = emptyList(),

    val selectedCollectionPos: Int? = null,
    val selectedSeriesPos: Int? = null,

    val layoutMode: LayoutMode = LayoutMode.VIEW,
    val viewMode: ViewMode = ViewMode.TEXT,
    val displayConfig: DisplayConfig = DisplayConfig.fromRange(DisplayRange.ONE_WEEK, null),
)

class PlanConstructorLayoutController(
    private val pageContext: PageContext,
    private val getTreatmentPlanUseCase: GetTreatmentPlanUseCase,
    private val getVersionContentUseCase: GetVersionContentUseCase,
    private val createTreatmentPlanUseCase: CreateTreatmentPlanUseCase,
    private val updateTreatmentPlanUseCase: UpdateTreatmentPlanUseCase,
    private val deleteTreatmentPlanUseCase: DeleteTreatmentPlanUseCase,
) {
    var activePlanBuilder: BasePlanBuilderNew<out BasePlan>? by mutableStateOf(null)

    var state by mutableStateOf(PlanConstructorLayoutState())
        private set

    var sideBarNavigationStrategy: (suspend (SideBarPlanElement) -> Unit)? = null
        private set

    var tooltipContentRenderStrategy: @Composable (BasePlanElement, Int) -> Unit = { _, _ -> }
        private set

    fun initializeNewPlan(newPlanType: PlanType) {
        state = state.copy(isLoadingPlan = true)
        val newPlan = when (newPlanType) {
            PlanType.PREPLAN -> PreparationPlan(
                title = "",
                description = "",
            )

            PlanType.POSTPLAN -> TreatmentPlan(
                title = "",
                description = "",
            )
        }
        this.activePlanBuilder = PlanBuildersNew.fromPlan(newPlan)
        refreshStateFromBuilder()
        refreshSideBarItems()
        state = state.copy(isLoadingPlan = false, isLoadingPlanContent = false)
    }

    fun refreshSideBarItems() {
        val builder = activePlanBuilder ?: return
        val newItems = mutableListOf<SideBarPlanElement>()
        builder.collections().sortedBy { it.relativeDay }.forEachIndexed { indexCollection, collection ->
            val collectionItem = SideBarPlanElement.CollectionItem(indexCollection, collection)
            newItems.add(collectionItem)

            collection.series.sortedBy { it.relativeDay }.forEachIndexed { indexSeries, series ->
                val seriesItem = SideBarPlanElement.NestedSeriesItem(indexCollection, indexSeries, collection, series)
                newItems.add(seriesItem)
            }
        }
        builder.directSeries().sortedBy { it.relativeDay }.forEachIndexed { indexSeries, series ->
            val seriesItem = SideBarPlanElement.RootSeriesItem(indexSeries, series)
            newItems.add(seriesItem)
        }
        state = state.copy(sideBarItems = newItems)
    }

    suspend fun ensurePlanLoaded(planId: String) {
        if (activePlanBuilder?.firebaseDocId() == planId) return

        activePlanBuilder = null
        state = state.copy(isLoadingPlan = true)

        try {
            getTreatmentPlanUseCase(planId).onSuccess { version ->
                activePlanBuilder = PlanBuildersNew.treatmentPlan()
                state = state.copy(
                    title = version.metadata?.fields?.title ?: "",
                    description = version.metadata?.fields?.description ?: "",
                    isLoadingPlan = false,
                    isLoadingPlanContent = true,
                )
                val plan = getVersionContentUseCase(version)
                activePlanBuilder = PlanBuildersNew.fromPlan(plan)
                refreshStateFromBuilder()
                refreshSideBarItems()
            }.onFailure {
                throw Exception()
            }
        } catch (e: Exception) {
            console.error(e)
        } finally {
            state = state.copy(isLoadingPlan = false, isLoadingPlanContent = false)
        }
    }

    suspend fun handleEvent(event: PlanConstructorLayoutEvent) {
        when (event) {
            is PlanConstructorLayoutEvent.ChangeLayoutMode -> onLayoutModeChanged(event.mode)
            is PlanConstructorLayoutEvent.ChangeViewMode -> onViewModeChanged(event.mode)
            is PlanConstructorLayoutEvent.ChangeDisplayRange -> onDisplayRangeChanged(event.range)
            is PlanConstructorLayoutEvent.NavigateDisplayForward -> onDisplayNavigateForward()
            is PlanConstructorLayoutEvent.NavigateDisplayBackward -> onDisplayNavigateBackward()
            is PlanConstructorLayoutEvent.UpdatePlanTitle -> onPlanTitleUpdated(event.newTitle)
            is PlanConstructorLayoutEvent.UpdatePlanDescription -> onPlanDescriptionUpdated(event.newDescription)
            is PlanConstructorLayoutEvent.UpdateImageSource -> onPlanImageSourceUpdated(event.newSource)
            is PlanConstructorLayoutEvent.Save -> onSave()
            is PlanConstructorLayoutEvent.Send -> onSend()
            is PlanConstructorLayoutEvent.SelectSideBarElement -> onSideBarElementSelected(event.element)
        }
    }

    fun onLayoutModeChanged(newMode: LayoutMode) {
        pageContext.navigateTo(
            currentPathWithParams(Route.Params.LAYOUT_MODE to newMode.name)
        )
    }

    fun onViewModeChanged(newMode: ViewMode) {
        pageContext.navigateTo(
            currentPathWithParams(Route.Params.VIEW_MODE to newMode.name)
        )
    }

    fun onDisplayRangeChanged(newRange: DisplayRange) {
        val newConfig = DisplayConfig.fromRange(newRange, activePlanBuilder, 0)
        pageContext.navigateTo(
            currentPathWithParams(
                Route.Params.DISPLAY_RANGE to newRange.name,
                Route.Params.DISPLAY_OFFSET to newConfig.offsetFromDayZero.toString()
            )
        )
    }

    fun onDisplayNavigateForward() {
        val newConfig = state.displayConfig.navigateForward()
        pageContext.navigateTo(
            currentPathWithParams(Route.Params.DISPLAY_OFFSET to newConfig.offsetFromDayZero.toString())
        )
    }

    fun onDisplayNavigateBackward() {
        val newConfig = state.displayConfig.navigateBackward()
        pageContext.navigateTo(
            currentPathWithParams(Route.Params.DISPLAY_OFFSET to newConfig.offsetFromDayZero.toString())
        )
    }

    fun onPlanTitleUpdated(newTitle: String) {
        val builder = activePlanBuilder ?: return
        builder.setTitle(newTitle)
        refreshStateFromBuilder()
    }

    fun onPlanDescriptionUpdated(newDescription: String) {
        val builder = activePlanBuilder ?: return
        builder.setDescription(newDescription)
        refreshStateFromBuilder()
    }

    fun onPlanImageSourceUpdated(newSource: String?) {
        val builder = activePlanBuilder ?: return
        builder.setImageUrl(newSource)
        refreshStateFromBuilder()
    }

    suspend fun onSave() {
        val builder = activePlanBuilder ?: return
        val plan = builder.rebuild()

        val result = when (val sourceId = builder.firebaseDocId()) {
            null -> createTreatmentPlanUseCase(plan)
            else -> updateTreatmentPlanUseCase(plan, sourceId)
        }

        result.onSuccess { savedPlan ->
            val newId = savedPlan.firebaseDocId!!
            val basePath = Route.WithParams.toPlanPreview(newId)
            builder.setFirebaseDocId(newId)
            builder.markSaved()
            refreshStateFromBuilder()
            pageContext.navigateTo(
                "$basePath${currentPageParams()}"
            )
        }.onFailure {  }
    }

    suspend fun onSend() {
        val builder = activePlanBuilder ?: return
        val sourceId = builder.firebaseDocId()
        val isPlanAdjusted = builder.isChanged

        if(!isPlanAdjusted && sourceId != null) {
            pageContext.navigateTo(Route.WithParams.toPlanDistribution(sourceId))
        } else {
            val plan = builder.rebuild()
            createTreatmentPlanUseCase(plan).onSuccess { temporalPlan ->
                val temporalPlanId = temporalPlan.firebaseDocId!!
                println("InTEMPORAL: $temporalPlanId")
                deleteTreatmentPlanUseCase(temporalPlanId).onSuccess {
                    pageContext.navigateTo(
                        Route.WithParams.toPlanDistribution(planId = temporalPlanId)
                    )
                }
            }.onFailure {  }
        }

    }

    suspend fun onSideBarElementSelected(element: SideBarPlanElement) {
        sideBarNavigationStrategy?.invoke(element) ?: console.warn("No nav strategy set for the page")
    }

    fun setSideBarNavigationStrategy(strategy: (suspend (SideBarPlanElement) -> Unit)?) {
        sideBarNavigationStrategy = strategy
    }

    fun setTooltipContentRenderStrategy(newTooltipStrategy: @Composable (BasePlanElement, Int) -> Unit) {
        tooltipContentRenderStrategy = newTooltipStrategy
    }

    fun getTooltipContentRenderStrategy(): @Composable (BasePlanElement, Int) -> Unit {
        return tooltipContentRenderStrategy
    }

    fun markSelection(seriesPos: Int?, collectionPos: Int?) {
        state = state.copy(
            selectedSeriesPos = seriesPos,
            selectedCollectionPos = collectionPos,
        )
    }

    fun updateStateFromUrl(params: Map<String, String>) {
        val layoutMode = when (params[Route.Params.LAYOUT_MODE]?.uppercase()) {
            LayoutMode.EDIT.name -> LayoutMode.EDIT
            LayoutMode.VIEW.name -> LayoutMode.VIEW
            else -> LayoutMode.VIEW
        }

        val viewMode = when (params[Route.Params.VIEW_MODE]?.uppercase()) {
            ViewMode.TEXT.name -> ViewMode.TEXT
            ViewMode.GRAPHICS.name -> ViewMode.GRAPHICS
            else -> ViewMode.TEXT
        }

        val displayRange = params[Route.Params.DISPLAY_RANGE]?.let {
            try {
                DisplayRange.valueOf(it.uppercase())
            } catch (e: IllegalArgumentException) {
                DisplayRange.ONE_WEEK
            }
        } ?: DisplayRange.ONE_WEEK
        
        val displayOffset = params[Route.Params.DISPLAY_OFFSET]?.toIntOrNull() ?: 0
        
        val displayConfig = DisplayConfig.fromRange(displayRange, activePlanBuilder, displayOffset)

        state = state.copy(
            layoutMode = layoutMode,
            viewMode = viewMode,
            displayConfig = displayConfig,
        )
    }

    private fun refreshStateFromBuilder() {
        val builder = activePlanBuilder ?: return
        state = state.copy(
            title = builder.title() ?: "",
            description = builder.description() ?: "",
            imageSrc = builder.imageUrl(),
            isPlanChanged = builder.isChanged,
            isPlanValid = builder.validate().isEmpty()
        )
    }
}

@Composable
fun rememberPlanConstructorLayoutController(
    pageContext: PageContext,
): PlanConstructorLayoutController {
    val getTreatmentPlanUseCase = injectAndRemember<GetTreatmentPlanUseCase>()
    val getVersionContentUseCase = injectAndRemember<GetVersionContentUseCase>()
    val createTreatmentPlanUseCase = injectAndRemember<CreateTreatmentPlanUseCase>()
    val updateTreatmentPlanUseCase = injectAndRemember<UpdateTreatmentPlanUseCase>()
    val deleteTreatmentPlanUseCase = injectAndRemember<DeleteTreatmentPlanUseCase>()
    return remember {
        PlanConstructorLayoutController(
            pageContext = pageContext,
            getTreatmentPlanUseCase = getTreatmentPlanUseCase,
            getVersionContentUseCase = getVersionContentUseCase,
            createTreatmentPlanUseCase = createTreatmentPlanUseCase,
            updateTreatmentPlanUseCase = updateTreatmentPlanUseCase,
            deleteTreatmentPlanUseCase = deleteTreatmentPlanUseCase,
        )
    }
}
